// "Day Care" — the OPD doctor's order for a day-care course (spec Q2/Q3).
//
// One order can cover a course: the service, a weekly pattern, an end (a number
// of sessions or a date) and standing instructions. A one-off is a course of
// one. The Day Care unit books the sessions from it on its board.
//
// Like the old OT request section, this posts on its own button rather than
// riding the prescription save: it is an action in another department. And it
// fetches NOTHING until the doctor presses "Open Day Care orders" — the OT
// sections were taken out of this console for calling the server on every
// consultation, and the console opens every section expanded, so being
// expanded is not the doctor asking for day care.
//
// The ordering doctor is resolved by the server from the login, never sent.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { toast } from "react-toastify";

import { changeOrder, createOrder, fetchOrders, fetchUnits, stopOrder } from "../api/daycareApi";
import { courseSummary, groupLabel } from "../utils/sessionView";
import { CHART_TYPE, END_MODE, GROUP_CLEAN, MARKER_VALUES, ORDER_STATUS, WEEKDAY_LABELS } from "../constants";
import { IPD_COLORS } from "../../ipd/utils";
import { StyledTextField, theme } from "../../doctor-console/StyledComponents";
import { istDayKey } from "../../../utils/istDateTime";

const LABEL_SX = { fontSize: 12, fontWeight: 600, color: IPD_COLORS.textSecondary, mb: 0.5 };
const MARKERS = [
  { key: "hbsag", label: "HBsAg" },
  { key: "anti_hcv", label: "Anti-HCV" },
  { key: "hiv", label: "HIV" },
];

function Field({ label, children, span = 1 }) {
  return (
    <Box sx={{ gridColumn: `span ${span}` }}>
      {label && <Typography sx={LABEL_SX}>{label}</Typography>}
      {children}
    </Box>
  );
}

function blankForm(unit) {
  return {
    unit_id: unit?.id || "",
    medical_service_id: unit?.services?.[0]?.medical_service_id || "",
    first_date: istDayKey(new Date()),
    repeat_days: [],
    end_mode: END_MODE.COUNT,
    session_count: "1",
    end_date: "",
    preferred_shift: unit?.shifts?.[0]?.code || "",
    instructions: "",
    hbsag: "",
    anti_hcv: "",
    hiv: "",
    markers_tested_on: "",
  };
}

/** The request body: blanks become null so the server decides what is missing. */
function orderBody(form, patientId) {
  const body = {
    patient_id: patientId,
    unit_id: form.unit_id,
    medical_service_id: form.medical_service_id,
    first_date: form.first_date,
    repeat_days: form.repeat_days,
    end_mode: form.end_mode,
    preferred_shift: form.preferred_shift,
    instructions: form.instructions.trim() || null,
    markers_tested_on: form.markers_tested_on || null,
  };
  MARKERS.forEach(({ key }) => { body[key] = form[key] || null; });
  if (form.end_mode === END_MODE.COUNT) body.session_count = form.session_count;
  else body.end_date = form.end_date || null;
  return body;
}

function MarkerFields({ form, setForm, idPrefix }) {
  return (
    <>
      {MARKERS.map(({ key, label }) => (
        <Field key={key}>
          <StyledTextField
            id={`${idPrefix}-${key}`}
            select
            fullWidth
            label={label}
            value={form[key]}
            onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          >
            {MARKER_VALUES.map((v) => (
              <MenuItem key={v} value={v}>{v === "POSITIVE" ? "Positive" : "Negative"}</MenuItem>
            ))}
          </StyledTextField>
        </Field>
      ))}
      <Field>
        <StyledTextField
          id={`${idPrefix}-markers-tested-on`}
          fullWidth
          type="date"
          label="Markers tested on"
          InputLabelProps={{ shrink: true }}
          value={form.markers_tested_on}
          onChange={(e) => setForm((f) => ({ ...f, markers_tested_on: e.target.value }))}
        />
      </Field>
    </>
  );
}

function EndFields({ form, setForm, idPrefix }) {
  return (
    <>
      <Field>
        <StyledTextField
          id={`${idPrefix}-end-mode`}
          select
          fullWidth
          label="Course ends"
          value={form.end_mode}
          onChange={(e) => setForm((f) => ({ ...f, end_mode: e.target.value }))}
        >
          <MenuItem value={END_MODE.COUNT}>After a number of sessions</MenuItem>
          <MenuItem value={END_MODE.DATE}>On a date (review)</MenuItem>
        </StyledTextField>
      </Field>
      <Field>
        {form.end_mode === END_MODE.COUNT ? (
          <StyledTextField
            id={`${idPrefix}-count`}
            fullWidth
            type="number"
            label="Sessions"
            inputProps={{ min: 1, max: 100 }}
            value={form.session_count}
            onChange={(e) => setForm((f) => ({ ...f, session_count: e.target.value }))}
          />
        ) : (
          <StyledTextField
            id={`${idPrefix}-end-date`}
            fullWidth
            type="date"
            label="Until"
            InputLabelProps={{ shrink: true }}
            value={form.end_date}
            onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
          />
        )}
      </Field>
    </>
  );
}

function ChangeDialog({ order, unit, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({
    instructions: order.instructions || "",
    end_mode: order.end_mode,
    session_count: String(order.session_count || ""),
    end_date: order.end_date || "",
    hbsag: order.hbsag || "",
    anti_hcv: order.anti_hcv || "",
    hiv: order.hiv || "",
    markers_tested_on: order.markers_tested_on || "",
  }));
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    const body = {
      instructions: form.instructions.trim() || null,
      end_mode: form.end_mode,
      session_count: form.end_mode === END_MODE.COUNT ? form.session_count : null,
      end_date: form.end_mode === END_MODE.DATE ? form.end_date || null : null,
      markers_tested_on: form.markers_tested_on || null,
    };
    MARKERS.forEach(({ key }) => { body[key] = form[key] || null; });
    const res = await changeOrder(order.id, body);
    setBusy(false);
    if (!res.ok) {
      toast.error(res.message || "Could not update the course");
      return;
    }
    toast.success(res.message || "Course updated");
    onSaved();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Change course</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2, pt: 1 }}>
          <EndFields form={form} setForm={setForm} idPrefix="daycare-change" />
          {unit?.chart_type === CHART_TYPE.DIALYSIS && <MarkerFields form={form} setForm={setForm} idPrefix="daycare-change" />}
          <Field span={2}>
            <StyledTextField
              id="daycare-change-instructions"
              fullWidth
              multiline
              minRows={2}
              label="Instructions for the unit"
              value={form.instructions}
              onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
            />
          </Field>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" disabled={busy} onClick={save}>Save changes</Button>
      </DialogActions>
    </Dialog>
  );
}

function StopDialog({ order, onClose, onStopped }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const stop = async () => {
    setBusy(true);
    const res = await stopOrder(order.id, reason.trim());
    setBusy(false);
    if (!res.ok) {
      toast.error(res.message || "Could not stop the course");
      return;
    }
    toast.success(res.message || "Course stopped");
    onStopped();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Stop this course</DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary, mb: 1.5 }}>
          Sessions booked after today are cancelled. Today's session is left to the unit.
        </Typography>
        <StyledTextField
          id="daycare-stop-reason"
          fullWidth
          multiline
          minRows={2}
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button color="error" variant="contained" disabled={busy || reason.trim().length < 3} onClick={stop}>
          Stop course
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DaycareOrderSection({ activeSection, patientId }) {
  const [units, setUnits] = useState([]);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState(blankForm(null));
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [changing, setChanging] = useState(null);
  const [stopping, setStopping] = useState(null);
  const [opened, setOpened] = useState(false);
  const loadedRef = useRef(false);

  const loadOrders = useCallback(async () => {
    try {
      setOrders(await fetchOrders({ patient_id: patientId }));
    } catch (err) {
      setOrders([]);
      toast.error(err?.body?.message || "Could not load this patient's day care courses");
    }
  }, [patientId]);

  useEffect(() => {
    if (!opened || !activeSection || !patientId || loadedRef.current) return;
    loadedRef.current = true;
    setLoading(true);
    Promise.all([
      fetchUnits()
        .then((list) => {
          setUnits(list || []);
          setForm(blankForm((list || [])[0]));
        })
        .catch((err) => toast.error(err?.body?.message || "Could not load the day care units")),
      loadOrders(),
    ]).finally(() => setLoading(false));
  }, [opened, activeSection, patientId, loadOrders]);

  const unit = units.find((u) => u.id === form.unit_id) || null;

  const toggleDay = (day) =>
    setForm((f) => ({
      ...f,
      repeat_days: f.repeat_days.includes(day)
        ? f.repeat_days.filter((d) => d !== day)
        : [...f.repeat_days, day].sort(),
    }));

  const send = async () => {
    setBusy(true);
    const res = await createOrder(orderBody(form, patientId));
    setBusy(false);
    if (!res.ok) {
      toast.error(res.message || "Could not send the order to Day Care");
      return;
    }
    toast.success(res.message || "Sent to Day Care");
    setForm(blankForm(unit));
    loadOrders();
  };

  if (!patientId) {
    return <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>Choose a patient before ordering day care.</Typography>;
  }
  if (!opened) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Button variant="outlined" size="small" onClick={() => setOpened(true)}>Open Day Care orders</Button>
        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>
          Order dialysis or another day-care course, or see this patient&apos;s courses.
        </Typography>
      </Box>
    );
  }
  if (loading) return <CircularProgress size={18} />;
  if (!units.length) {
    return <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>No day care unit is set up for this hospital.</Typography>;
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
        <Field>
          <StyledTextField
            id="daycare-unit"
            select
            fullWidth
            label="Unit"
            value={form.unit_id}
            onChange={(e) => setForm(blankForm(units.find((u) => u.id === e.target.value)))}
          >
            {units.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
          </StyledTextField>
        </Field>
        <Field span={2}>
          <StyledTextField
            id="daycare-service"
            select
            fullWidth
            label="Service"
            value={form.medical_service_id}
            onChange={(e) => setForm((f) => ({ ...f, medical_service_id: e.target.value }))}
          >
            {(unit?.services || []).map((s) => (
              <MenuItem key={s.medical_service_id} value={s.medical_service_id}>{s.name}</MenuItem>
            ))}
          </StyledTextField>
        </Field>
        <Field>
          <StyledTextField
            id="daycare-shift"
            select
            fullWidth
            label="Preferred shift"
            value={form.preferred_shift}
            onChange={(e) => setForm((f) => ({ ...f, preferred_shift: e.target.value }))}
          >
            {(unit?.shifts || []).map((s) => (
              <MenuItem key={s.code} value={s.code}>{`${s.label} (${s.start}-${s.end})`}</MenuItem>
            ))}
          </StyledTextField>
        </Field>

        <Field>
          <StyledTextField
            id="daycare-first-date"
            fullWidth
            type="date"
            label="First session"
            InputLabelProps={{ shrink: true }}
            value={form.first_date}
            onChange={(e) => setForm((f) => ({ ...f, first_date: e.target.value }))}
          />
        </Field>
        <Field label="Repeat on (leave empty for a single session)" span={3}>
          <ToggleButtonGroup size="small" value={form.repeat_days} aria-label="Repeat on">
            {WEEKDAY_LABELS.map((label, day) => (
              <ToggleButton key={label} value={day} onClick={() => toggleDay(day)}
                sx={{ px: 1.5, textTransform: "none", fontSize: 12.5 }}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Field>

        {form.repeat_days.length > 0 && <EndFields form={form} setForm={setForm} idPrefix="daycare" />}

        {unit?.chart_type === CHART_TYPE.DIALYSIS && <MarkerFields form={form} setForm={setForm} idPrefix="daycare" />}

        <Field span={4}>
          <StyledTextField
            id="daycare-instructions"
            fullWidth
            label="Instructions for the unit"
            placeholder="e.g. dry weight 58 kg, AVF left arm, heparin 2000 U bolus + 500 U/h"
            value={form.instructions}
            onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
          />
        </Field>
      </Box>

      <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 2 }}>
        <Button variant="contained" size="small" disabled={busy} onClick={send}>Send to Day Care</Button>
        <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>
          The unit books the sessions and admits the patient as Day Care.
        </Typography>
      </Box>

      {orders.length > 0 && (
        <Box sx={{ mt: 2.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 1, letterSpacing: "0.03em" }}>
            THIS PATIENT&apos;S DAY CARE COURSES
          </Typography>
          {orders.map((o) => (
            <Box
              key={o.id}
              sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", py: 1, borderBottom: `1px solid ${IPD_COLORS.border}` }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{courseSummary(o)}</Typography>
              <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                {`${o.unit_name || ""} · ${o.sessions_admitted} done · ${o.sessions_booked} booked`}
                {o.sessions_missed ? ` · ${o.sessions_missed} missed` : ""}
              </Typography>
              {((o.patient_group && o.patient_group !== GROUP_CLEAN) || o.retest_due) && (
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.warning.text }}>
                  {[o.patient_group !== GROUP_CLEAN && groupLabel(o.patient_group), o.retest_due && "markers retest due"]
                    .filter(Boolean).join(" · ")}
                </Typography>
              )}
              {o.status === ORDER_STATUS.ACTIVE ? (
                <>
                  <Button size="small" onClick={() => setChanging(o)}>Change</Button>
                  <Button size="small" color="error" onClick={() => setStopping(o)}>Stop</Button>
                </>
              ) : (
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textMuted }}>{`Stopped — ${o.stop_reason || ""}`}</Typography>
              )}
            </Box>
          ))}
        </Box>
      )}

      {changing && (
        <ChangeDialog
          order={changing}
          unit={units.find((u) => u.id === changing.unit_id)}
          onClose={() => setChanging(null)}
          onSaved={() => { setChanging(null); loadOrders(); }}
        />
      )}
      {stopping && (
        <StopDialog
          order={stopping}
          onClose={() => setStopping(null)}
          onStopped={() => { setStopping(null); loadOrders(); }}
        />
      )}
    </ThemeProvider>
  );
}
